import { useEffect, useRef } from 'react'

const W = 320
const PERIOD = 3.0
const K = 6
const SYL = 4
const X0 = 20
const X1 = 300
const Y_TOP = 40 // high pitch, drawn near the top
const Y_BOTTOM = 200 // low pitch, drawn lower

const EMOTIONS = [
  { label: 'Happy', color: [255, 210, 58], energy: 0.6, ctrl: [0.5, 0.7, 0.6, 0.85, 0.7, 0.92], timing: [0.2, 0.42, 0.62, 0.82] },
  { label: 'Sad', color: [90, 155, 255], energy: 0.2, ctrl: [0.45, 0.4, 0.34, 0.3, 0.27, 0.2], timing: [0.12, 0.36, 0.62, 0.9] },
  { label: 'Angry', color: [255, 58, 82], energy: 1.0, ctrl: [0.6, 0.92, 0.55, 0.96, 0.58, 0.85], timing: [0.14, 0.3, 0.46, 0.6] },
  { label: 'Calm', color: [57, 224, 176], energy: 0.15, ctrl: [0.5, 0.52, 0.5, 0.53, 0.5, 0.51], timing: [0.2, 0.44, 0.66, 0.88] },
  { label: 'Sarcastic', color: [176, 123, 255], energy: 0.5, ctrl: [0.55, 0.75, 0.5, 0.38, 0.55, 0.82], timing: [0.16, 0.42, 0.66, 0.92] },
]
const N = 120

function contour(ctrl, u) {
  const seg = u * (K - 1)
  const i = Math.min(K - 2, Math.floor(seg))
  let f = seg - i
  f = f * f * (3 - 2 * f)
  const v = ctrl[i] + (ctrl[i + 1] - ctrl[i]) * f
  return Y_BOTTOM - v * (Y_BOTTOM - Y_TOP)
}

// Emotion lives in prosody, not the words: the fixed phrase "I'm fine" keeps
// its pitch (F0) contour morphing between emotions — happy rises and
// bounces, sad falls and flattens, angry is jagged and loud.
export function Scene10Emotion2D() {
  const pathRef = useRef()
  const labelRefs = useRef([])
  const sylRefs = useRef([])
  const cur = useRef({
    ctrl: [...EMOTIONS[0].ctrl], timing: [...EMOTIONS[0].timing],
    energy: EMOTIONS[0].energy, color: [...EMOTIONS[0].color],
  })
  const raf = useRef(0)

  useEffect(() => {
    let start = performance.now()
    const loop = (now) => {
      // `now` can be a hair earlier than `start` on the very first frame in
      // some browsers, making t negative — clamp so `ai` never goes negative.
      const t = Math.max(0, (now - start) / 1000)
      const ai = Math.floor(t / PERIOD) % EMOTIONS.length
      const target = EMOTIONS[ai]
      const s = cur.current
      for (let k = 0; k < K; k++) s.ctrl[k] += (target.ctrl[k] - s.ctrl[k]) * 0.05
      for (let k = 0; k < SYL; k++) s.timing[k] += (target.timing[k] - s.timing[k]) * 0.05
      s.energy += (target.energy - s.energy) * 0.05
      for (let c = 0; c < 3; c++) s.color[c] += (target.color[c] - s.color[c]) * 0.05
      const colorStr = `rgb(${s.color[0] | 0},${s.color[1] | 0},${s.color[2] | 0})`

      let d = ''
      for (let i = 0; i <= N; i++) {
        const u = i / N
        const x = X0 + (X1 - X0) * u
        const vibrato = Math.sin(x * 0.5 - t * 6) * 8 * s.energy
        const y = contour(s.ctrl, u) + vibrato
        d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `
      }
      if (pathRef.current) {
        pathRef.current.setAttribute('d', d)
        pathRef.current.setAttribute('stroke', colorStr)
      }

      const h = 10 + s.energy * 34
      sylRefs.current.forEach((el, k) => {
        if (!el) return
        const u = s.timing[k]
        const x = X0 + (X1 - X0) * u
        const y = contour(s.ctrl, u)
        el.setAttribute('x', (x - 3).toFixed(1))
        el.setAttribute('y', (y - h).toFixed(1))
        el.setAttribute('height', h.toFixed(1))
        el.setAttribute('fill', colorStr)
      })

      labelRefs.current.forEach((el, i) => { if (el) el.setAttribute('opacity', i === ai ? '1' : '0') })

      raf.current = requestAnimationFrame(loop)
    }
    raf.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  return (
    <svg viewBox="0 0 320 280" style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block', margin: '0 auto' }}>
      {EMOTIONS.map((e, i) => (
        <text key={e.label} ref={(el) => { labelRefs.current[i] = el }} x={W / 2} y="20"
          fill={`rgb(${e.color[0]},${e.color[1]},${e.color[2]})`} fontSize="18" fontFamily="system-ui, sans-serif"
          textAnchor="middle" opacity="0">
          {e.label}
        </text>
      ))}

      <line x1={X0 - 6} y1={Y_TOP - 10} x2={X0 - 6} y2={Y_BOTTOM + 10} stroke="#3a5a7a" strokeWidth="1" opacity="0.3" />
      <line x1={X0 - 6} y1={Y_BOTTOM + 10} x2={X1 + 6} y2={Y_BOTTOM + 10} stroke="#3a5a7a" strokeWidth="1" opacity="0.3" />

      {Array.from({ length: SYL }).map((_, k) => (
        <rect key={k} ref={(el) => { sylRefs.current[k] = el }} width="6" rx="2" />
      ))}

      <path ref={pathRef} fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />

      <text x={W / 2} y={Y_BOTTOM + 44} fill="#dfeaf2" fontSize="17" fontFamily="system-ui, sans-serif" textAnchor="middle">"I'm fine"</text>
      <text x={W / 2} y={Y_BOTTOM + 64} fill="#5d7891" fontSize="9" fontFamily="monospace" textAnchor="middle" letterSpacing="0.05em">same words · prosody = pitch · rhythm · energy</text>
    </svg>
  )
}
